/**
 * `GitHubIntegrationBlock` — renders one GitHub App installation as a
 * `<ProviderBlock>` with a live repo list fetched from the slice 1
 * endpoint (`GET /workspaces/:id/github/installations/:id/details`).
 *
 * One block per installation — the parent maps over the workspace's
 * GitHub integrations and mounts a block for each. The block owns its
 * own fetch lifecycle so each installation can be in an independent
 * loading / error state.
 *
 * Kebab menu (four items — spec §"What ships"):
 *   1. Re-authorize / refresh access — redirects to the backend
 *      reauthorize endpoint which 302s to GitHub's configure-repos page
 *      for THIS specific installation (not the install-picker). Loading
 *      state on the menu item prevents double-clicks during the
 *      browser hand-off.
 *   2. View on GitHub — anchor to the manage-installation URL,
 *      branched on account type. Org installs live under
 *      `github.com/organizations/<org>/settings/installations/<id>`;
 *      user installs under `github.com/settings/installations/<id>`.
 *      The flat `/settings/installations/<id>` form 404s for orgs
 *      (#1165 — bug sweep).
 *   3. Copy installation ID — clipboard write
 *   4. Disconnect — calls the existing delete handler via a destructive
 *      confirmation modal.
 *
 * Broken state: when the slice 1 endpoint returns an error (most common
 * cause: the App was uninstalled on GitHub's side before OttoTester's
 * webhook delivered), renders the "Disconnected" badge + a reconnect CTA
 * inside the repo body, matching mockup Step 05.
 */
import { useEffect, useState, useCallback } from 'react'
import {
  RefreshCw,
  ExternalLink,
  Copy,
  Trash2,
  Check,
} from 'lucide-react'
import { Button } from '@/components/ui'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import {
  ProviderBlock,
  ConnectedBadge,
  DisconnectedBadge,
  OwnerAvatar,
} from '@/components/ProviderBlock'
import {
  githubInstallationAPI,
  ciIntegrationsAPI,
  type CiIntegration,
  type GitHubInstallationAccount,
  type GitHubInstallationDetails,
} from '@/lib/api'
import { TriggerRulesForRepo } from './TriggerRulesForRepo'

/**
 * Build the GitHub "manage installation" URL, branching on account
 * type. Org installs MUST go through the `/organizations/<login>/`
 * sub-path; the flat `/settings/installations/<id>` form 404s for orgs
 * (#1165 — bug sweep).
 *
 * The `account` argument is optional so the helper degrades to the
 * user-shape URL on broken state (installation-details fetch failed,
 * account unknown). That's the same shape the old hardcoded URL used,
 * so the broken state behaves identically to pre-#1165 — wrong for
 * orgs, right for users, no regression for either over the
 * pre-#1165 baseline. The kebab menu's "View on GitHub" item is
 * still active in the broken state (it gates on `installationId`,
 * not `account`), so an org-install operator clicking it during a
 * broken state STILL hits the wrong URL — a future change could
 * either disable the kebab item when account is unknown, or retry
 * the details fetch on click. Out of scope for this slice; the
 * happy-path connected-state fix is the primary bug.
 */
export function buildManageOnGitHubUrl(
  installationId: number,
  account: GitHubInstallationAccount | null,
): string {
  if (account?.type === 'Organization') {
    return `https://github.com/organizations/${encodeURIComponent(account.login)}/settings/installations/${installationId}`
  }
  return `https://github.com/settings/installations/${installationId}`
}

// ── GitHub octocat SVG mark ──────────────────────────────────────────────

/** The GitHub octocat mark, confined to the 38×38 provider-mark tile.
 * SVG path is the same simplified outline used in the mockup. */
function GitHubMark() {
  return (
    <div className="w-[38px] h-[38px] rounded-lg bg-[#24292f] flex items-center justify-center">
      <svg viewBox="0 0 24 24" className="w-[22px] h-[22px] fill-white" aria-hidden="true">
        <path d="M12 .5C5.65.5.5 5.65.5 12c0 5.08 3.29 9.39 7.86 10.91.58.11.79-.25.79-.55 0-.27-.01-1.17-.02-2.12-3.2.69-3.87-1.36-3.87-1.36-.52-1.33-1.28-1.68-1.28-1.68-1.05-.71.08-.7.08-.7 1.16.08 1.77 1.19 1.77 1.19 1.03 1.76 2.69 1.25 3.35.95.1-.75.4-1.25.73-1.54-2.55-.29-5.24-1.28-5.24-5.71 0-1.26.45-2.29 1.19-3.1-.12-.29-.51-1.47.11-3.06 0 0 .97-.31 3.18 1.18.92-.26 1.91-.39 2.89-.39.98 0 1.97.13 2.89.39 2.21-1.49 3.18-1.18 3.18-1.18.62 1.59.23 2.77.11 3.06.74.81 1.19 1.84 1.19 3.1 0 4.44-2.7 5.41-5.27 5.7.41.36.78 1.06.78 2.14 0 1.55-.01 2.79-.01 3.17 0 .3.21.67.8.55C20.21 21.39 23.5 17.08 23.5 12 23.5 5.65 18.35.5 12 .5z" />
      </svg>
    </div>
  )
}

// ── Props ─────────────────────────────────────────────────────────────────

interface GitHubIntegrationBlockProps {
  /** The CI integration record for this GitHub installation. */
  integration: CiIntegration
  /** The workspace that owns this integration. Used for the re-authorize
   * URL and the installation-details API call. */
  workspaceId: string
  /** Called after a successful disconnect so the parent can reload the
   * integration list. */
  onDisconnected: () => void
  /** Called when an error occurs during an action so the parent can
   * surface it in a top-level error banner. */
  onError: (msg: string) => void
  /** Suppresses the inline per-repo `TriggerRulesForRepo` editor in the
   * repo list body. Set by callers that own connection-only surfaces
   * (the workspace Integrations tab post-#1153, where trigger-rule
   * editing lives in the App Integrations tab's per-variant drawer
   * instead — post-#1184; previously the standalone variant Automation
   * page). Defaults to `false` so any caller still owning the binding
   * surface — none today after #1153, kept for forward-compat — gets
   * the original behavior. */
  hideTriggerRules?: boolean
}

// ── Component ─────────────────────────────────────────────────────────────

export function GitHubIntegrationBlock({
  integration,
  workspaceId,
  onDisconnected,
  onError,
  hideTriggerRules = false,
}: GitHubIntegrationBlockProps) {
  // Parse installationId from providerConfig. Normally a number, but
  // guard against numeric-string serialization from older records.
  const rawId = integration.providerConfig?.installationId
  const installationId: number | null =
    typeof rawId === 'number'
      ? rawId
      : typeof rawId === 'string' && /^\d+$/.test(rawId)
        ? parseInt(rawId, 10)
        : null

  const [details, setDetails] = useState<GitHubInstallationDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState<string | null>(null)

  const [disconnecting, setDisconnecting] = useState(false)
  const [confirmDisconnect, setConfirmDisconnect] = useState(false)
  const [idCopied, setIdCopied] = useState(false)
  const [reauthorizing, setReauthorizing] = useState(false)

  const fetchDetails = useCallback(async () => {
    if (!installationId) {
      setFetchError('Installation ID is missing — reconnect to resolve this.')
      setLoading(false)
      return
    }
    setLoading(true)
    setFetchError(null)
    try {
      const data = await githubInstallationAPI.getDetails(workspaceId, installationId)
      setDetails(data)
    } catch (err) {
      setFetchError(err instanceof Error ? err.message : 'Unable to fetch installation details from GitHub.')
    } finally {
      setLoading(false)
    }
  }, [workspaceId, installationId])

  useEffect(() => {
    void fetchDetails()
  }, [fetchDetails])

  // Derive the account name for the subtitle — use live data when
  // available, fall back to the raw installation ID.
  const accountLogin = details?.account.login ?? null
  const accountType = details?.account.type ?? null
  const isBroken = !loading && !!fetchError

  const subtitle = loading ? (
    <span className="text-muted-foreground">Loading…</span>
  ) : accountLogin ? (
    <>
      Installed on <strong className="text-foreground">{accountLogin}</strong>
      {accountType ? ` · ${accountType}` : ''}
      {installationId ? ` · Installation #${installationId}` : ''}
    </>
  ) : installationId ? (
    <>Installation #{installationId}</>
  ) : (
    <>No installation ID</>
  )

  // ── Kebab menu items ───────────────────────────────────────────────────

  function handleReauthorize() {
    if (!installationId || reauthorizing) return
    // Redirect to the backend reauthorize endpoint. The backend 302s to
    // GitHub's configure-repos page with `installation_id` pinned to THIS
    // installation, so GitHub skips the account picker. We set
    // `reauthorizing` to disable the item during the hand-off (the browser
    // will navigate away shortly after this runs).
    setReauthorizing(true)
    window.location.assign(
      githubInstallationAPI.reauthorizeUrl(workspaceId, installationId),
    )
  }

  function handleViewOnGitHub() {
    if (!installationId) return
    // #1165 — branch on `account.type` so org installs hit the
    // org-scoped URL instead of 404ing on the flat user-shape URL.
    window.open(
      buildManageOnGitHubUrl(installationId, details?.account ?? null),
      '_blank',
      'noopener,noreferrer',
    )
  }

  async function handleCopyId() {
    if (!installationId) return
    try {
      await navigator.clipboard.writeText(String(installationId))
      setIdCopied(true)
      setTimeout(() => setIdCopied(false), 2000)
    } catch {
      // Clipboard unavailable — silently ignore (ID is visible in the subtitle)
    }
  }

  async function handleDisconnect() {
    setDisconnecting(true)
    try {
      await ciIntegrationsAPI.delete(integration.id)
      onDisconnected()
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to disconnect')
    } finally {
      setDisconnecting(false)
      setConfirmDisconnect(false)
    }
  }

  const menuItems = [
    {
      key: 'reauthorize',
      label: reauthorizing ? 'Redirecting to GitHub…' : 'Re-authorize / refresh access',
      icon: <RefreshCw className={`h-3.5 w-3.5 ${reauthorizing ? 'animate-spin' : ''}`} />,
      disabled: !installationId || reauthorizing,
      onClick: handleReauthorize,
    },
    {
      key: 'view-on-github',
      label: 'View on GitHub',
      icon: <ExternalLink className="h-3.5 w-3.5" />,
      disabled: !installationId,
      onClick: handleViewOnGitHub,
    },
    {
      key: 'copy-id',
      label: idCopied ? 'Copied!' : 'Copy installation ID',
      icon: idCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />,
      disabled: !installationId,
      onClick: () => { void handleCopyId() },
    },
    {
      key: 'disconnect',
      label: 'Disconnect',
      icon: <Trash2 className="h-3.5 w-3.5" />,
      danger: true,
      separator: true,
      onClick: () => setConfirmDisconnect(true),
    },
  ]

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <>
      <ProviderBlock
        mark={<GitHubMark />}
        name="GitHub"
        subtitle={subtitle}
        statusBadge={isBroken ? <DisconnectedBadge /> : <ConnectedBadge />}
        menuItems={menuItems}
        broken={isBroken}
      >
        <RepoListBody
          loading={loading}
          fetchError={fetchError}
          details={details}
          installationId={installationId}
          accountLogin={accountLogin}
          account={details?.account ?? null}
          integrationId={integration.id}
          onReconnect={handleReauthorize}
          onRemove={() => setConfirmDisconnect(true)}
          hideTriggerRules={hideTriggerRules}
        />
      </ProviderBlock>

      {/* Destructive confirmation — disconnect this installation. */}
      <ConfirmDialog
        open={confirmDisconnect}
        onClose={() => setConfirmDisconnect(false)}
        title="Disconnect GitHub?"
        description={
          accountLogin ? (
            <>
              OttoTester will stop posting check runs on{' '}
              <strong>{accountLogin}</strong>. The GitHub App remains
              installed on your GitHub account — you can reconnect it here
              any time.
            </>
          ) : (
            'OttoTester will stop posting check runs for this installation.'
          )
        }
        confirmLabel={disconnecting ? 'Disconnecting…' : 'Disconnect'}
        confirmVariant="destructive"
        onConfirm={handleDisconnect}
        busy={disconnecting}
      />
    </>
  )
}

// ── Repo list body ────────────────────────────────────────────────────────

/** The repo-list section that lives in the ProviderBlock body.
 * Handles three states: loading skeleton, error / broken state, and
 * the happy-path repo list with footer.
 *
 * Each repo row now includes TriggerRulesForRepo below it (#1134). The
 * component itself is provider-agnostic; the GitHub integration passes
 * provider='github' so the editor modal renders GitHub's event
 * vocabulary. */
function RepoListBody({
  loading,
  fetchError,
  details,
  installationId,
  accountLogin,
  account,
  integrationId,
  onReconnect,
  onRemove,
  hideTriggerRules,
}: {
  loading: boolean
  fetchError: string | null
  details: GitHubInstallationDetails | null
  installationId: number | null
  accountLogin: string | null
  /** Full account record — passed alongside `accountLogin` so the
   * "Manage on GitHub" link can branch on `account.type` (org installs
   * need the `/organizations/<login>/settings/...` URL shape; users
   * need the flat `/settings/...` form). #1165. */
  account: GitHubInstallationAccount | null
  /** The CiIntegration id — passed to TriggerRulesForRepo so new rules
   * can be created with the correct integrationId FK. */
  integrationId: string
  onReconnect: () => void
  onRemove: () => void
  /** When true, skip rendering the per-repo `TriggerRulesForRepo` editor
   * (the workspace Integrations tab is connection-only after #1153). */
  hideTriggerRules: boolean
}) {
  if (loading) {
    return (
      <div className="space-y-2 py-1">
        <div className="h-3 rounded bg-muted animate-pulse w-24 mb-3" />
        {[1, 2].map((i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-full bg-muted animate-pulse flex-shrink-0" />
            <div className="h-3 rounded bg-muted animate-pulse flex-1" />
          </div>
        ))}
      </div>
    )
  }

  if (fetchError) {
    return <BrokenState accountLogin={accountLogin} onReconnect={onReconnect} onRemove={onRemove} />
  }

  if (!details) return null

  const repos = details.repos
  const repoCount = repos.length

  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground py-2">
        {repoCount === 0 ? 'No repositories' : `${repoCount} ${repoCount === 1 ? 'Repository' : 'Repositories'}`}
      </div>

      {repos.length === 0 ? (
        <p className="text-xs text-muted-foreground py-1">
          No repositories are accessible via this installation. Grant access to repositories on GitHub.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {repos.map((repo) => {
            const repoIdentifier = `${repo.owner}/${repo.name}`
            return (
              <li
                key={repoIdentifier}
                className="rounded border border-border bg-background overflow-hidden"
              >
                {/* Repo header row */}
                <div
                  className="grid items-center gap-3 py-2 px-3"
                  style={{ gridTemplateColumns: '28px 1fr' }}
                >
                  <OwnerAvatar
                    login={repo.owner}
                    avatarUrl={repo.ownerAvatarUrl}
                    size={28}
                  />
                  <span className="font-mono text-sm truncate">
                    <span className="text-muted-foreground">{repo.owner} /</span>{' '}
                    {repo.name}
                  </span>
                </div>
                {/* Trigger rules for this repo (#1134). Rendered below
                    the repo header row; component handles its own
                    loading state. Suppressed when `hideTriggerRules`
                    is set — the workspace Integrations tab after #1153
                    is connection-only, with trigger-rule editing in
                    the App Integrations tab's per-variant drawer
                    (post-#1184; previously a standalone Automation
                    page). */}
                {!hideTriggerRules && (
                  <div className="border-t border-border/60 bg-muted/20">
                    <TriggerRulesForRepo
                      provider="github"
                      integrationId={integrationId}
                      repoIdentifier={repoIdentifier}
                    />
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {/* Footer: count summary + Manage on GitHub link */}
      <div className="flex items-center justify-between mt-2 pt-2.5 border-t border-dashed border-border">
        <span className="text-xs text-muted-foreground">
          Access granted to {repoCount} {repoCount === 1 ? 'repository' : 'repositories'}
        </span>
        {installationId && (
          <a
            // #1165 — `account`-aware URL so org installs route to the
            // org-scoped settings page instead of 404ing on the flat
            // user-shape URL.
            href={buildManageOnGitHubUrl(installationId, account)}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-primary hover:underline inline-flex items-center gap-1"
          >
            Manage on GitHub <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
    </div>
  )
}

/** The body rendered when the details fetch fails — broken state (mockup
 * Step 05). Renders the "App uninstalled on GitHub" banner with reconnect
 * and remove actions. The Manage-on-GitHub footer link is suppressed in
 * this state because the broken path renders `<BrokenState>` instead of
 * the repo list. */
function BrokenState({
  accountLogin,
  onReconnect,
  onRemove,
}: {
  accountLogin: string | null
  onReconnect: () => void
  onRemove: () => void
}) {
  return (
    <div className="mt-1 mb-1 rounded p-3 bg-destructive/10 border border-destructive/40 text-destructive text-xs flex flex-wrap items-center justify-between gap-3">
      <span>
        <strong>GitHub App is no longer installed</strong>
        {accountLogin && (
          <>
            {' '}on{' '}
            <code className="font-mono">{accountLogin}</code>
          </>
        )}
        . Reconnect to resume posting checks, or remove this row.
      </span>
      <span className="flex gap-2 flex-shrink-0">
        <Button size="sm" onClick={onReconnect}>
          Reconnect
        </Button>
        <Button size="sm" variant="destructive" onClick={onRemove}>
          Remove
        </Button>
      </span>
    </div>
  )
}
